import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit,
    Renderer2, ViewChild
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import * as moment from 'moment';

import { TaskServiceProxy, UpdateTaskDatesDto } from '@shared/service-proxies/service-proxies';
import {
    DatePresetSpec, ResolvedDatePreset, TaskDatePresetsService
} from '@shared/common/task-date-presets/task-date-presets.service';
import { DataService } from '@app/shared/common/auth/data.service';
import { TaskDateNudgeRequest, TaskDateNudgeService } from './task-date-nudge.service';

/**
 * Which face of the card is showing. Deliberately separate from `leaving`: when the two were one
 * `'ask' | 'done' | 'out'` field, dismissing from the success state set it to `'out'`, which no longer
 * matched `'done'` — so the card flipped back to the question for the 240ms leave animation before
 * disappearing. The content must not change just because the card is on its way out.
 */
type NudgeContent = 'ask' | 'done';

/**
 * `vf-task-date-nudge` — the dark card that drops in at top-centre after a task is saved with an
 * assignee but no Start/End date. One glance, one tap, gone.
 *
 * Non-modal by design: no backdrop, no click-away layer, nothing behind it is blocked. It leaves on its
 * own after ~9s, or on `×` / `Leave it undated` / `Esc`. Dismissing changes nothing about the task.
 *
 * Hosted by `MainLayoutComponent` (`STANDARDS.md` §1.13) so it works on every main route — a task can be
 * created from `/my-day`, `/calendar`, a project or a contact, and the card has to survive all of them.
 * Raised through `TaskDateNudgeService`, not the `DataService` bus (§1.12).
 *
 * Design: `Trovve.dc.html` 517-582 (markup), 5586-5657 (behaviour), 164-179 (motion).
 */
@Component({
    selector: 'vf-task-date-nudge',
    templateUrl: './task-date-nudge.component.html',
    styleUrls: ['./task-date-nudge.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskDateNudgeComponent implements OnInit, OnDestroy {

    /** The card leaves on its own after this long, tracked by the amber bar. */
    private static readonly AUTO_DISMISS_MS = 9000;
    /** How long the green confirmation stays up before the card closes itself. */
    private static readonly DONE_MS = 1700;
    /** How long the card stays mounted while leaving. Slightly longer than the SCSS `$leave-ms` (220ms)
     *  so the animation finishes before the element is removed. */
    private static readonly LEAVE_MS = 240;

    /** The three one-tap offers. Resolved against the company calendar by `TaskDatePresetsService`. */
    private static readonly PRESETS: DatePresetSpec[] = [
        { key: 'today', label: 'Today', offsetDays: 0 },
        { key: 'tomorrow', label: 'Tomorrow', offsetDays: 1 },
        { key: 'in2', label: 'In 2 days', offsetDays: 2 }
    ];

    @ViewChild('timerBar') timerBarRef: ElementRef<HTMLElement>;

    protected destroy$ = new Subject<void>();

    /** The card is in the DOM. */
    visible = false;
    /** Which face is showing. Never changes while the card is leaving. */
    content: NudgeContent = 'ask';
    /** The leave animation is playing; the card is removed when it finishes. */
    leaving = false;
    /** A date is being written back; guards a double-tap from firing two updates. */
    saving = false;
    /**
     * The pill whose date is being written. It stays lit while `saving` so the pause before the green
     * check reads as "applying this one" rather than "everything went disabled".
     */
    pendingKey: string | null = null;
    /** The inline calendar is expanded (which also pauses the auto-dismiss). */
    calendarOpen = false;
    /** Set when the write-back fails, so the card never falsely claims success. */
    errorMessage = '';

    // ── Precomputed view model. No logic in templates (`STANDARDS.md` §1.5). ──
    title = '';
    subLine = '';
    doneTitle = '';
    doneSubLine = '';
    streakLabel = '';
    options: ResolvedDatePreset[] = [];

    /** Company-calendar "today" — task dates float on the company calendar, never the browser's. */
    today: moment.Moment = moment().startOf('day');

    private request: TaskDateNudgeRequest | null = null;
    private assigneeFirstName = '';
    private assigneeIsMe = false;
    private dismissTimer: number | null = null;
    private leaveTimer: number | null = null;

    constructor(
        private _nudge: TaskDateNudgeService,
        private _taskService: TaskServiceProxy,
        private _presets: TaskDatePresetsService,
        private _dataService: DataService,
        private _renderer: Renderer2,
        private _cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this._nudge.requests$
            .pipe(takeUntil(this.destroy$))
            .subscribe((request: TaskDateNudgeRequest) => this.show(request));
    }

    ngOnDestroy(): void {
        this.clearTimers();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ─────────────────────────── open / close ───────────────────────────

    private show(request: TaskDateNudgeRequest): void {
        this.clearTimers();
        this.request = request;
        this.assigneeFirstName = this.firstNameOf(request.assigneeName);
        this.assigneeIsMe = !!request.assigneeIsCurrentUser;
        this.today = this._presets.today();
        this.content = 'ask';
        this.leaving = false;
        this.visible = true;
        this.saving = false;
        this.pendingKey = null;
        this.calendarOpen = false;
        this.errorMessage = '';
        this.buildAskVm();
        this.startDismissTimer();
        this._cdr.markForCheck();
    }

    /** `×`, `Leave it undated`, `Esc` and the auto-dismiss all land here. The task is left untouched. */
    dismiss(): void {
        if (!this.visible || this.leaving) {
            return;
        }
        this.clearTimers();
        // `content` is left alone — whichever face is up stays up and fades out with the card.
        this.leaving = true;
        this._cdr.markForCheck();
        this.leaveTimer = window.setTimeout(() => {
            this.visible = false;
            this.request = null;
            this.leaveTimer = null;
            this._cdr.markForCheck();
        }, TaskDateNudgeComponent.LEAVE_MS);
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.dismiss();
    }

    // ─────────────────────────── choosing a date ───────────────────────────

    /** A `Today` / `Tomorrow` / `In 2 days` pill. */
    pickOption(option: ResolvedDatePreset): void {
        this.pendingKey = option.key;
        this.applyDate(option.date.clone());
    }

    /** A day from the inline calendar. */
    pickCalendarDate(date: moment.Moment | undefined): void {
        if (!date) {
            return;
        }
        this.applyDate(moment(date.format('YYYY-MM-DD'), 'YYYY-MM-DD'));
    }

    toggleCalendar(): void {
        this.calendarOpen = !this.calendarOpen;
        // Opening pauses the countdown so the user isn't rushed through a calendar; closing restarts it.
        if (this.calendarOpen) {
            this.clearDismissTimer();
        } else {
            this.startDismissTimer();
        }
        this._cdr.markForCheck();
    }

    closeCalendar(): void {
        if (!this.calendarOpen) {
            return;
        }
        this.calendarOpen = false;
        this.startDismissTimer();
        this._cdr.markForCheck();
    }

    /** Any interaction with the card restarts the countdown — the timer must never rush a live user. */
    onInteract(): void {
        if (this.content !== 'ask' || this.leaving || this.calendarOpen || this.saving) {
            return;
        }
        this.startDismissTimer();
    }

    trackByOption(_index: number, option: ResolvedDatePreset): string {
        return option.key;
    }

    private applyDate(date: moment.Moment): void {
        const request = this.request;
        if (!request || this.saving || this.content !== 'ask' || this.leaving) {
            return;
        }
        this.clearTimers();
        this.saving = true;
        this.errorMessage = '';
        this._cdr.markForCheck();

        const input = new UpdateTaskDatesDto();
        input.taskIds = [request.taskId];
        // `.utc(true)` keeps the wall-clock day across the round trip — task dates float on the company
        // calendar, so the browser's offset must not shift them. Same payload shape as the calendar and
        // timeline drag-to-reschedule.
        input.startDate = date.clone().utc(true).toISOString();
        input.dueDate = date.clone().utc(true).toISOString();

        this._taskService.updateTaskDates(input)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: result => {
                    this.saving = false;
                    if (result && result.isError) {
                        this.failed(result.errorMessage);
                        return;
                    }
                    this.succeeded(date);
                },
                error: () => {
                    this.saving = false;
                    this.failed('');
                }
            });
    }

    private succeeded(date: moment.Moment): void {
        const todayKey = this.today.format('YYYY-MM-DD');
        const streak = this._nudge.recordDated(todayKey);

        this.calendarOpen = false;
        this.pendingKey = null;
        this.content = 'done';
        this.doneTitle = 'Dated for ' + date.format('MMM D') + '.';
        this.doneSubLine = (this.assigneeIsMe ? 'It’ll be on your list that day. ' : this.assigneeFirstName + ' will see it on the day. ')
            + (streak >= 3 ? 'You’re on a roll.' : 'Small thing, big difference.');
        this._cdr.markForCheck();

        // The lists that could be showing this task now have a stale row. Reuse the existing bus
        // channels rather than adding one (`STANDARDS.md` §1.12 / §1.13).
        this._dataService.refreshAgenda.next(false);
        this._dataService.refreshProject.next();

        this.dismissTimer = window.setTimeout(() => {
            this.dismissTimer = null;
            this.dismiss();
        }, TaskDateNudgeComponent.DONE_MS);
    }

    private failed(message: string): void {
        this.pendingKey = null;
        this.errorMessage = message || 'Couldn’t save that date. Please try again.';
        // Stay on the question with the error showing, and give the user the full window back.
        this.startDismissTimer();
        this._cdr.markForCheck();
    }

    // ─────────────────────────── view model ───────────────────────────

    private buildAskVm(): void {
        const request = this.request;
        const name = this.assigneeFirstName;
        const taskName = request ? request.taskName : 'This task';

        this.title = this.assigneeIsMe
            ? 'No due date? It won’t show up in your day.'
            : 'No due date? ' + name + ' can’t plan around it.';
        this.subLine = '“' + taskName + '” is saved — give it a day so it '
            + (this.assigneeIsMe ? 'lands in your week.' : 'shows up in ' + name + '’s week.');

        this.options = this._presets.resolve(TaskDateNudgeComponent.PRESETS, this.today);

        const streak = this._nudge.streak(this.today.format('YYYY-MM-DD'));
        this.streakLabel = streak >= 2
            ? streak + ' dated in a row · nice'
            : (streak === 1 ? '1 dated today' : '');
    }

    private firstNameOf(fullName: string): string {
        const trimmed = (fullName || '').trim();
        if (!trimmed) {
            return 'They';
        }
        return trimmed.split(/\s+/)[0];
    }

    // ─────────────────────────── timers ───────────────────────────

    private startDismissTimer(): void {
        this.clearDismissTimer();
        this.dismissTimer = window.setTimeout(
            () => {
                this.dismissTimer = null;
                this.dismiss();
            },
            TaskDateNudgeComponent.AUTO_DISMISS_MS
        );
        this.restartTimerBar();
    }

    private clearDismissTimer(): void {
        if (this.dismissTimer !== null) {
            clearTimeout(this.dismissTimer);
            this.dismissTimer = null;
        }
    }

    private clearTimers(): void {
        this.clearDismissTimer();
        if (this.leaveTimer !== null) {
            clearTimeout(this.leaveTimer);
            this.leaveTimer = null;
        }
    }

    /**
     * Replay the amber bar's drain from full. A CSS animation only restarts when the element re-enters
     * the document flow, so the class comes off, layout is flushed, and it goes back on — the standard
     * `@ViewChild` + `Renderer2` route (`STANDARDS.md` §1.6 bans reaching for the DOM by selector).
     */
    private restartTimerBar(): void {
        const el = this.timerBarRef ? this.timerBarRef.nativeElement : null;
        if (!el) {
            return;
        }
        this._renderer.removeClass(el, 'vf-tdn__bar-fill--run');
        // Reading a layout property forces the removal to be committed before the class is re-added.
        void el.offsetWidth;
        this._renderer.addClass(el, 'vf-tdn__bar-fill--run');
    }
}
